import Store from '../store';
import addOrEditAddressService from '@api/routes/account/AddOrEditCustomerAddress';
import getAddresses from '@api/routes/account/GetCustomerAddresses';
import deleteAddress from '@api/routes/account/DeleteCustomerAddress';
import setAddressAsPrimary from '@api/routes/account/SetCustomerAddressAsPrimary';
import { statusCodes, logUpsServiceError, showSuggestions} from '../../components/address-validation/address-validation';

/** 
 * @typedef {Object} StoreContext
 * @property {Function} commit
 * @property {Function} dispatch
 * @property {Object<string,any} getters
 * @property {Object<string,any} state
 * 
 * @typedef {Object} AddOrEditResponse
 * @property {boolean} cancelled
 * @property {Object<string,any>} data
 * @property {string} error
 * @property {boolean} success
 * @property {boolean} showSuggestionPrompt
 */

const store = new Store({
    state: {
        addresses: [],
        lastChanged: {
            addressId: null,
            index: -1
        },
        loaded: false,
        loading: false,
        loadingForm: false,
        loadingLine: null
    },
    actions: {
        /**
         * @typedef {{ addressId: string, addressParty: Object<string,any> }} AddOrEditAddressPayload
         * 
         * @param {StoreContext} context 
         * @param {AddOrEditAddressPayload} payload 
         * @returns {AddOrEditResponse}
         */
        async addOrEditAddress({ commit, state }, { addressId, addressParty, isUserConfirmed }) {
            if(state.loadingForm) {
                return { cancelled: true };
            }

            let showSuggestionPrompt = false;

            commit('SET_LOADING_FORM');
            commit('SET_LOADING_LINE', addressId);

            const payload = { 
                AddressId: addressId,
                AddressParty: addressParty,
                ...(isUserConfirmed ? { IsSuggestion: true } : {})
            };

            const response = await addOrEditAddressService.post(payload);

            if(response.success) {
                const { UpsAddressValidationStatus: validationStatus } = response.data;
                showSuggestionPrompt = showSuggestions(validationStatus);

                if(validationStatus === statusCodes.UPS_UNAVAILABLE) {
                    logUpsServiceError();
                }

                commit('SET_LAST_CHANGED', addressId);
                commit('SET_ADDRESSES', response.data.Parties);
            }
            
            commit('SET_LOADING_FORM', false);
            commit('SET_LOADING_LINE');

            return {
                ...response,
                cancelled: false,
                showSuggestionPrompt
            };
        },
        async getAddresses({ commit, state }) {
            if(state.loading) {
                return { cancelled: true };
            }
            
            commit('SET_LOADING');

            const response = await getAddresses.get();

            if(response.success) {
                //set primary address first in array
                let addresses = [...response.data];
                let isPrimaryIndex = response.data.findIndex(address => address.IsPrimary == true );
                addresses.unshift(addresses.splice(isPrimaryIndex, 1)[0]);

                commit('SET_ADDRESSES', addresses);
                commit('SET_LOADED');
            }

            commit('SET_LOADING', false);

            return response;
        },
        async deleteAddress({ commit }, addressId) {
            commit('SET_LOADING_LINE', addressId);

            const payload = { AddressId: addressId };
            const response = await deleteAddress.post(payload);

            if(response.success) {
                commit('SET_LAST_CHANGED', addressId);
                commit('SET_ADDRESSES', response.data.Parties);
            }
            
            commit('SET_LOADING_LINE');

            return response;
        },
        async setPrimary({ commit }, addressId) {
            commit('SET_LOADING_LINE', addressId);

            const payload = { AddressId: addressId };
            const response = await setAddressAsPrimary.post(payload);

            if(response.success) {
                commit('SET_LAST_CHANGED', addressId);
                commit('SET_ADDRESSES', response.data.Parties);
            }
            
            commit('SET_LOADING_LINE');

            return response;
        },
    },
    getters: {
        addressIds(state) {
            if(state.addresses[0] === undefined){
                return [];
            }
            return state.addresses.map(({ AddressName }) => AddressName);
        },
        addressInfo: (state) => (addressId) => {
            if(state.addresses[0] === undefined){
                return {};
            }
            const address = state.addresses.find(({ AddressName }) => AddressName === addressId);
            return address ? {
                address1: address.Address1,
                address2: address.Address2,
                city: address.City,
                country: address.Country,
                firstName: address.FirstName,
                isPrimary: address.IsPrimary,
                lastName: address.LastName,
                name: [ address.FirstName, address.LastName ].join(' ')?.trim(),
                phone: address.PhoneNumber,
                state: address.State,
                stateCode: address.StateCode,
                zip: address.ZipPostalCode
            } : {};
        },
        isPrimary: (state) => (addressId) => {
            const address = state.addresses.find(({ AddressName }) => AddressName === addressId);
            return address ? address.IsPrimary : false;
        },
        isLoadingLine: (state) => (addressId) => {
            return addressId === state.loadingLine;
        },
        wasChanged(state) {
            return !!state.lastChanged.addressId;
        }
    },
    mutations: {
        SET_ADDRESSES(state, addresses) {
            state.addresses = addresses || [];
        },
        SET_LAST_CHANGED(state, addressId) {
            if(state.address === undefined || state.address[0] === undefined){
                return {};
            }
            const index = state.addresses.findIndex(({ AddressName }) => AddressName === addressId);
            state.lastChanged = {
                addressId,
                index
            };
        },
        SET_LOADED(state, loaded = true) {
            state.loaded = loaded;
        },
        SET_LOADING(state, loading = true) {
            state.loading = loading;
        },
        SET_LOADING_FORM(state, loadingForm = true) {
            state.loadingForm = loadingForm;
        },
        SET_LOADING_LINE(state, lineId = null) {
            state.loadingLine = lineId;
        }
    }
});

export {
    logUpsServiceError,
    showSuggestions,
    statusCodes as validationStatusCodes
};

export const {
    actions,
    mutations,
    state,
    watch,
} = store;

export default store;
